/**
 * AML Quantitative Risk Model
 * Learns from transaction history and analyst feedback to predict money laundering risk
 */

export class AMLModel {
  constructor(config = {}) {
    this.version = '1.0.0';
    this.lastUpdated = new Date();

    // Feature weights (learned from feedback, updated over time)
    this.weights = {
      // Amount-based features
      amountNormalized: 0.15,        // Large amounts
      structuringScore: 0.12,         // Multiple small txs
      roundAmountScore: 0.08,         // Suspiciously round

      // Entity risk features
      walletRisk: 0.18,              // Known mixer/darknet/sanctioned
      jurisdictionRisk: 0.14,         // High-risk country

      // Velocity & pattern features
      velocityScore: 0.10,            // Rapid transaction sequence
      outlierScore: 0.10,             // Deviation from baseline
      newEntityScore: 0.07,           // First time counterparty

      // Network features
      firstHopRisk: 0.06,             // Direct from risky source

      // Temporal features
      anomalousTimingScore: 0.04,     // Unusual hours/patterns
    };

    this.config = {
      minConfidenceThreshold: 0.5,
      alertThreshold: 70,             // Risk score >= 70 generates alert
      flagThreshold: 50,              // Score >= 50 is flagged for review
      ...config,
    };

    // Historical baseline for anomaly detection
    this.baseline = {
      avgAmount: 5000,
      avgFrequency: 5,                // txs per day
      avgJurisdictions: 3,            // unique countries per week
    };
  }

  /**
   * Score a single transaction
   * Returns { riskScore, components, explanation, shouldAlert }
   */
  scoreTransaction(tx, historicalContext = {}) {
    const components = {};
    let totalWeightedScore = 0;
    let totalWeight = 0;

    // ─── AMOUNT FEATURES ──────────────────────────────────────────────────
    const amountFeature = this._scoreAmount(tx);
    components.amountNormalized = amountFeature;
    totalWeightedScore += amountFeature * this.weights.amountNormalized;
    totalWeight += this.weights.amountNormalized;

    // ─── STRUCTURING DETECTION ────────────────────────────────────────────
    const structuringScore = this._scoreStructuring(tx, historicalContext);
    components.structuringScore = structuringScore;
    totalWeightedScore += structuringScore * this.weights.structuringScore;
    totalWeight += this.weights.structuringScore;

    // ─── ROUND AMOUNTS ────────────────────────────────────────────────────
    const roundScore = this._scoreRoundAmounts(tx);
    components.roundAmountScore = roundScore;
    totalWeightedScore += roundScore * this.weights.roundAmountScore;
    totalWeight += this.weights.roundAmountScore;

    // ─── ENTITY RISK ──────────────────────────────────────────────────────
    const walletRisk = this._scoreWalletRisk(tx);
    components.walletRisk = walletRisk;
    totalWeightedScore += walletRisk * this.weights.walletRisk;
    totalWeight += this.weights.walletRisk;

    // ─── JURISDICTION RISK ────────────────────────────────────────────────
    const jurisdictionScore = this._scoreJurisdiction(tx);
    components.jurisdictionRisk = jurisdictionScore;
    totalWeightedScore += jurisdictionScore * this.weights.jurisdictionRisk;
    totalWeight += this.weights.jurisdictionRisk;

    // ─── VELOCITY ANALYSIS ────────────────────────────────────────────────
    const velocityScore = this._scoreVelocity(tx, historicalContext);
    components.velocityScore = velocityScore;
    totalWeightedScore += velocityScore * this.weights.velocityScore;
    totalWeight += this.weights.velocityScore;

    // ─── ANOMALY DETECTION ────────────────────────────────────────────────
    const outlierScore = this._scoreOutlier(tx, historicalContext);
    components.outlierScore = outlierScore;
    totalWeightedScore += outlierScore * this.weights.outlierScore;
    totalWeight += this.weights.outlierScore;

    // ─── NEW ENTITY ────────────────────────────────────────────────────────
    const newEntityScore = this._scoreNewEntity(tx, historicalContext);
    components.newEntityScore = newEntityScore;
    totalWeightedScore += newEntityScore * this.weights.newEntityScore;
    totalWeight += this.weights.newEntityScore;

    // ─── TEMPORAL ANOMALIES ───────────────────────────────────────────────
    const timingScore = this._scoreTiming(tx);
    components.anomalousTimingScore = timingScore;
    totalWeightedScore += timingScore * this.weights.anomalousTimingScore;
    totalWeight += this.weights.anomalousTimingScore;

    // Normalize to 0-100
    const riskScore = Math.round((totalWeightedScore / totalWeight) * 100);

    return {
      riskScore,
      components,
      shouldAlert: riskScore >= this.config.alertThreshold,
      isFlagged: riskScore >= this.config.flagThreshold,
      confidence: this._calculateConfidence(components),
      explanation: this._generateExplanation(components, riskScore),
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Learn from feedback: analyst marked alert as true/false positive
   * Updates weights to improve future predictions
   */
  learnFromFeedback(feedback) {
    const { txId, riskScore, isTruePositive, components, analystNotes } = feedback;

    // Simple learning: adjust weights based on correctness
    // If true positive with high score: reinforce those weights
    // If false positive with high score: reduce those weights

    const learningRate = 0.05;
    const correction = isTruePositive ? 1 : -1;

    for (const [feature, weight] of Object.entries(this.weights)) {
      if (components[feature] !== undefined) {
        const componentScore = components[feature];
        // Reinforce features that were correct, penalize incorrect ones
        const adjustment = learningRate * correction * componentScore;
        this.weights[feature] = Math.max(0, Math.min(1, weight + adjustment));
      }
    }

    // Normalize weights to sum to 1
    const totalWeight = Object.values(this.weights).reduce((a, b) => a + b, 0);
    for (const key of Object.keys(this.weights)) {
      this.weights[key] = this.weights[key] / totalWeight;
    }

    this.lastUpdated = new Date();

    return {
      success: true,
      feedback: { txId, isTruePositive, timestamp: new Date().toISOString() },
      updatedWeights: this.weights,
    };
  }

  /**
   * Update baseline from clean historical data
   */
  updateBaseline(cleanTransactions) {
    if (!cleanTransactions || cleanTransactions.length === 0) return;

    const amounts = cleanTransactions.map(t => parseFloat(t.amount) || 0);
    const frequencies = cleanTransactions.map(t => t.frequencyPerDay || 0);

    this.baseline = {
      avgAmount: amounts.reduce((a, b) => a + b, 0) / amounts.length,
      avgFrequency: frequencies.reduce((a, b) => a + b, 0) / frequencies.length,
      stdDevAmount: this._calculateStdDev(amounts),
      stdDevFrequency: this._calculateStdDev(frequencies),
    };
  }

  // ─── FEATURE SCORING FUNCTIONS ────────────────────────────────────────

  _scoreAmount(tx) {
    const amount = parseFloat(tx.amount) || 0;
    const normalized = Math.min(amount / 50000, 1); // Cap at $50k
    return normalized * 100;
  }

  _scoreStructuring(tx, ctx) {
    if (!ctx.recentTransactions) return 0;

    const window = 7; // days
    const recentTxs = ctx.recentTransactions.filter(
      t => this._daysAgo(t.date) <= window
    );

    if (recentTxs.length < 3) return 0;

    const totalAmount = recentTxs.reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const maxAmount = Math.max(...recentTxs.map(t => parseFloat(t.amount)));

    // Structuring = many small txs totaling large amount
    if (maxAmount < 10000 && totalAmount >= 25000) {
      return Math.min(100, (totalAmount / 50000) * 100);
    }

    return 0;
  }

  _scoreRoundAmounts(tx) {
    const amount = parseFloat(tx.amount) || 0;
    if (amount < 5000) return 0;

    // Check if round to nearest 1000
    if (amount % 1000 === 0) return 50;
    if (amount % 5000 === 0) return 75;
    if (amount % 10000 === 0) return 100;

    return 0;
  }

  _scoreWalletRisk(tx) {
    // Use entity risk scores from enrichment
    const fromRisk = parseFloat(tx.fromRiskScore) || 0;
    const toRisk = parseFloat(tx.toRiskScore) || 0;
    return Math.max(fromRisk, toRisk);
  }

  _scoreJurisdiction(tx) {
    const HIGH_RISK = ['KP', 'IR', 'SY', 'CU']; // North Korea, Iran, Syria, Cuba
    const jurisdiction = tx.toCountry || tx.fromCountry || '';

    if (HIGH_RISK.includes(jurisdiction)) return 100;

    // FATF Grey List (simplified)
    const GREY_LIST = ['PK', 'VN', 'TR', 'UZ'];
    if (GREY_LIST.includes(jurisdiction)) return 60;

    return 0;
  }

  _scoreVelocity(tx, ctx) {
    if (!ctx.txCountPastDay) return 0;

    const expectedDaily = this.baseline.avgFrequency || 5;
    const actualDaily = ctx.txCountPastDay;

    // Rapid sequence detection
    if (actualDaily > expectedDaily * 3) {
      return Math.min(100, (actualDaily / (expectedDaily * 5)) * 100);
    }

    return 0;
  }

  _scoreOutlier(tx, ctx) {
    const amount = parseFloat(tx.amount) || 0;
    const expectedAmount = this.baseline.avgAmount || 5000;
    const stdDev = this.baseline.stdDevAmount || expectedAmount * 0.5;

    const zScore = Math.abs((amount - expectedAmount) / stdDev);
    if (zScore > 3) return 100; // 3 standard deviations
    if (zScore > 2) return 70;
    if (zScore > 1) return 40;

    return 0;
  }

  _scoreNewEntity(tx, ctx) {
    if (ctx.isFirstTimeEntity) return 40;
    return 0;
  }

  _scoreTiming(tx) {
    const hour = new Date(tx.date).getHours();
    // Off-hours (11pm - 6am) slightly elevated risk
    if (hour >= 23 || hour < 6) return 20;
    return 0;
  }

  _calculateConfidence(components) {
    const validComponents = Object.values(components).filter(v => v !== undefined);
    if (validComponents.length === 0) return 0;

    // Confidence based on agreement of multiple signals
    const average = validComponents.reduce((a, b) => a + b, 0) / validComponents.length;
    const variance = validComponents.reduce((sum, v) => sum + Math.pow(v - average, 2), 0) / validComponents.length;
    const stdDev = Math.sqrt(variance);

    // Lower std dev = higher confidence (signals agree)
    return Math.max(0, Math.min(100, 100 - stdDev));
  }

  _generateExplanation(components, riskScore) {
    const findings = [];

    if (components.walletRisk > 50) {
      findings.push(`High-risk wallet detected (${Math.round(components.walletRisk)})`);
    }
    if (components.structuringScore > 40) {
      findings.push('Potential structuring pattern detected');
    }
    if (components.velocityScore > 50) {
      findings.push('Unusual transaction velocity');
    }
    if (components.outlierScore > 60) {
      findings.push('Transaction amount is statistical outlier');
    }
    if (components.jurisdictionRisk > 50) {
      findings.push('High-risk jurisdiction involved');
    }

    return findings.length > 0
      ? findings.join('; ')
      : 'Low risk. Transaction appears normal.';
  }

  _calculateStdDev(values) {
    if (values.length === 0) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
  }

  _daysAgo(date) {
    return (new Date() - new Date(date)) / (1000 * 60 * 60 * 24);
  }

  /**
   * Export model state for persistence
   */
  serialize() {
    return {
      version: this.version,
      weights: this.weights,
      baseline: this.baseline,
      config: this.config,
      lastUpdated: this.lastUpdated,
    };
  }

  /**
   * Import saved model state
   */
  static deserialize(data) {
    const model = new AMLModel(data.config);
    model.weights = data.weights;
    model.baseline = data.baseline;
    model.lastUpdated = new Date(data.lastUpdated);
    return model;
  }
}

export default AMLModel;
